import React from "react";
import { useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useNavigate, useParams } from "react-router-dom";
import {
  BusinessCategoryLike,
  getDisplayedBusinessTopLevelCategories,
  isHealthcareCategoryName,
} from "../../../core/business/category";
import { BUSINESSES_PAGED, GET_BUSINESS_CATEGORIES } from "../../../platform/graphql/queries";
import { BusinessBasic, BusinessData } from "../../../platform/types";
import CategoryBusinessCard from "../../../platform/pages/customer/categoryBusinessCard";
import SeoMeta from "../../../platform/components/common/seoMeta";
import {
  getAvailableBookingSubcategories,
  getBookingCategoryDisplayName,
  getResolvedBookingBusinessSubcategory,
  getResolvedBookingBusinessSubcategoryId,
  getResolvedBookingBusinessTopCategory,
  getResolvedBookingBusinessTopCategoryId,
} from "../utils/bookingCategoryFilters";

type BookingListingFilter = "all" | "event" | "service" | "class";

const BOOKING_LISTING_FILTERS: { value: BookingListingFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "event", label: "Events" },
  { value: "service", label: "Services" },
  { value: "class", label: "Classes" },
];

const BOOKING_TYPE_CATEGORY_BY_FILTER: Record<Exclude<BookingListingFilter, "all">, number> = {
  event: 1,
  service: 2,
  class: 3,
};

type BookingMarketplaceBusiness = BusinessData &
  Partial<BusinessBasic> & {
  businessCategoryId?: number | null;
  businessSubcategoryId?: number | null;
  businessTopCategory?: BusinessCategoryLike | null;
  businessSubcategory?: BusinessCategoryLike | null;
  displayOnBookingPage?: boolean;
};

function hasPublicBookingListing(business: BookingMarketplaceBusiness | any) {
  const bookingTypes =
    business?.businessBookingTypes ?? business?.businessBasic?.businessBookingTypes ?? [];

  return (
    (business?.businessBasic?.displayOnBookingPage === true ||
      business?.displayOnBookingPage === true) &&
    bookingTypes.some((bookingType: any) => bookingType?.isPublic === true)
  );
}

function matchesBookingListingFilter(
  business: BookingMarketplaceBusiness | any,
  filter: BookingListingFilter
) {
  if (filter === "all") return hasPublicBookingListing(business);

  const bookingTypes =
    business?.businessBookingTypes ?? business?.businessBasic?.businessBookingTypes ?? [];
  const targetCategoryId = BOOKING_TYPE_CATEGORY_BY_FILTER[filter];

  return (Array.isArray(bookingTypes) ? bookingTypes : []).some(
    (bookingType: any) =>
      bookingType?.isPublic === true &&
      Number(bookingType?.bookingTypeCategoryId || 0) === Number(targetCategoryId)
  );
}

function matchesBusinessCategoryFilter(
  business: BookingMarketplaceBusiness | any,
  categoryById: Map<number, BusinessCategoryLike>,
  healthcareCategory: BusinessCategoryLike | null,
  selectedCategoryId: number | null,
  selectedSubcategoryId: number | null
) {
  if (selectedSubcategoryId) {
    return (
      getResolvedBookingBusinessSubcategoryId(business, categoryById) ===
      selectedSubcategoryId
    );
  }
  if (selectedCategoryId) {
    return (
      getResolvedBookingBusinessTopCategoryId(
        business,
        categoryById,
        healthcareCategory
      ) ===
      selectedCategoryId
    );
  }
  return true;
}

export default function BookingCategoryPage(): JSX.Element {
  const theme = useTheme();
  const navigate = useNavigate();
  const { category } = useParams<{ category: string }>();
  const routeCategoryId = Number(category?.split("_")[0]) || null;
  const routeCategoryLabel = String(category?.split("_").slice(1).join(" ") || "")
    .replace(/_/g, " ")
    .trim();

  const searchParams = new URLSearchParams(window.location.search);
  const city = searchParams.get("city") || "";
  const [cityIdText, ...cityLabelParts] = city.split("_");
  const cityIdValue = Number(cityIdText);
  const cityId = Number.isFinite(cityIdValue) && cityIdValue > 0 ? cityIdValue : undefined;
  const cityLabel = cityId
    ? cityLabelParts.join("_").replace(/_/g, " ").trim()
    : String(city || "").trim();
  const currentCountryId = Number(localStorage.getItem("currentCountryId") || "1");

  const [listingFilter, setListingFilter] = React.useState<BookingListingFilter>("all");
  const [selectedCategoryId, setSelectedCategoryId] = React.useState<number | null>(routeCategoryId);
  const [selectedSubcategoryId, setSelectedSubcategoryId] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const html = document.documentElement;
    const body = document.body;
    const previousHtmlScrollbarWidth = html.style.getPropertyValue("scrollbar-width");
    const previousBodyScrollbarWidth = body.style.getPropertyValue("scrollbar-width");
    const previousMsOverflowStyle = body.style.getPropertyValue("-ms-overflow-style");

    html.style.setProperty("scrollbar-width", "none");
    body.style.setProperty("scrollbar-width", "none");
    body.style.setProperty("-ms-overflow-style", "none");

    const styleTag = document.createElement("style");
    styleTag.setAttribute("data-booking-public-scrollbar", "category");
    styleTag.textContent = `
      html::-webkit-scrollbar,
      body::-webkit-scrollbar {
        width: 0 !important;
        height: 0 !important;
        display: none !important;
        background: transparent !important;
      }
    `;
    document.head.appendChild(styleTag);

    return () => {
      if (previousHtmlScrollbarWidth) {
        html.style.setProperty("scrollbar-width", previousHtmlScrollbarWidth);
      } else {
        html.style.removeProperty("scrollbar-width");
      }
      if (previousBodyScrollbarWidth) {
        body.style.setProperty("scrollbar-width", previousBodyScrollbarWidth);
      } else {
        body.style.removeProperty("scrollbar-width");
      }
      if (previousMsOverflowStyle) {
        body.style.setProperty("-ms-overflow-style", previousMsOverflowStyle);
      } else {
        body.style.removeProperty("-ms-overflow-style");
      }
      styleTag.remove();
    };
  }, []);

  const { data: categoriesData } = useQuery(GET_BUSINESS_CATEGORIES);
  const { loading, data } = useQuery(BUSINESSES_PAGED, {
    variables: {
      countryId: currentCountryId,
      cityId,
      limit: 200,
      offset: 0,
    },
    skip: !cityId,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-and-network",
  });

  const categoryById = React.useMemo(
    () =>
      new Map<number, BusinessCategoryLike>(
        (categoriesData?.getBusinessCategories || []).map((categoryItem: any) => [
          Number(categoryItem?.id || 0),
          categoryItem as BusinessCategoryLike,
        ])
      ),
    [categoriesData]
  );

  const healthcareTopCategory = React.useMemo(
    () =>
      (
        getDisplayedBusinessTopLevelCategories(
          (categoriesData?.getBusinessCategories || []) as BusinessCategoryLike[]
        ) || []
      ).find((categoryItem) => isHealthcareCategoryName(String(categoryItem?.name || "").trim())) || null,
    [categoriesData]
  );

  const cityBusinesses = React.useMemo(
    () =>
      ((data?.businessesPaged?.items || []) as BookingMarketplaceBusiness[]).filter(
        hasPublicBookingListing
      ),
    [data]
  );

  const listingFilteredBusinesses = React.useMemo(
    () =>
      cityBusinesses.filter((business: BookingMarketplaceBusiness) =>
        matchesBookingListingFilter(business, listingFilter)
      ),
    [cityBusinesses, listingFilter]
  );

  const availableTopCategories = React.useMemo(() => {
    const topLevelById = new Map<number, BusinessCategoryLike>();
    const availableTopCategoryIds = new Set<number>();

    listingFilteredBusinesses.forEach((business: BookingMarketplaceBusiness) => {
      const topCategoryId = getResolvedBookingBusinessTopCategoryId(
        business,
        categoryById,
        healthcareTopCategory
      );
      if (topCategoryId) {
        availableTopCategoryIds.add(topCategoryId);
      }
    });

    getDisplayedBusinessTopLevelCategories(
      (categoriesData?.getBusinessCategories || []) as BusinessCategoryLike[]
    ).forEach((topCategory) => {
      const categoryId = Number(topCategory?.id || 0);
      if (availableTopCategoryIds.has(categoryId)) {
        topLevelById.set(categoryId, topCategory);
      }
    });

    listingFilteredBusinesses.forEach((business: BookingMarketplaceBusiness) => {
      const topCategoryId = getResolvedBookingBusinessTopCategoryId(
        business,
        categoryById,
        healthcareTopCategory
      );
      if (!topCategoryId || topLevelById.has(topCategoryId)) return;

      const fallbackCategory =
        categoryById.get(topCategoryId) ||
        getResolvedBookingBusinessTopCategory(
          business,
          categoryById,
          healthcareTopCategory
        ) ||
        null;
      if (fallbackCategory) {
        topLevelById.set(topCategoryId, fallbackCategory);
      }
    });

    return Array.from(topLevelById.values()).sort((left, right) =>
      getBookingCategoryDisplayName(left).localeCompare(
        getBookingCategoryDisplayName(right)
      )
    );
  }, [categoriesData, categoryById, healthcareTopCategory, listingFilteredBusinesses]);

  const selectedCategorySubcategories = React.useMemo(() => {
    return getAvailableBookingSubcategories({
      selectedCategoryId,
      categories: (categoriesData?.getBusinessCategories || []) as BusinessCategoryLike[],
      categoryById,
      healthcareCategory: healthcareTopCategory,
      businesses: listingFilteredBusinesses,
    });
  }, [
    categoriesData,
    categoryById,
    healthcareTopCategory,
    listingFilteredBusinesses,
    selectedCategoryId,
  ]);

  const selectedCategoryLabel = React.useMemo(() => {
    if (!selectedCategoryId) return "";

    const selectedCategory =
      availableTopCategories.find(
        (categoryItem) => Number(categoryItem?.id || 0) === Number(selectedCategoryId)
      ) || categoryById.get(Number(selectedCategoryId));

    return getBookingCategoryDisplayName(selectedCategory);
  }, [availableTopCategories, categoryById, selectedCategoryId]);

  const selectedSubcategoryLabel = React.useMemo(() => {
    if (!selectedSubcategoryId) return "";

    const selectedSubcategory =
      selectedCategorySubcategories.find(
        (subcategory) => Number(subcategory?.id || 0) === Number(selectedSubcategoryId)
      ) ||
      getResolvedBookingBusinessSubcategory(
        { businessSubcategoryId: selectedSubcategoryId },
        categoryById
      ) ||
      categoryById.get(Number(selectedSubcategoryId));

    return getBookingCategoryDisplayName(selectedSubcategory);
  }, [categoryById, selectedCategorySubcategories, selectedSubcategoryId]);

  const filteredBusinesses = React.useMemo(
    () =>
      listingFilteredBusinesses.filter((business: BookingMarketplaceBusiness) =>
        matchesBusinessCategoryFilter(
          business,
          categoryById,
          healthcareTopCategory,
          selectedCategoryId,
          selectedSubcategoryId
        )
      ),
    [
      categoryById,
      healthcareTopCategory,
      listingFilteredBusinesses,
      selectedCategoryId,
      selectedSubcategoryId,
    ]
  );

  React.useEffect(() => {
    setSelectedCategoryId(routeCategoryId);
    setSelectedSubcategoryId(null);
  }, [routeCategoryId]);

  React.useEffect(() => {
    if (
      selectedCategoryId &&
      !availableTopCategories.some(
        (categoryItem) => Number(categoryItem?.id || 0) === Number(selectedCategoryId)
      )
    ) {
      setSelectedCategoryId(routeCategoryId && categoryById.has(routeCategoryId) ? routeCategoryId : null);
      setSelectedSubcategoryId(null);
    }
  }, [availableTopCategories, categoryById, routeCategoryId, selectedCategoryId]);

  React.useEffect(() => {
    if (
      selectedSubcategoryId &&
      !selectedCategorySubcategories.some(
        (subcategory) => Number(subcategory?.id || 0) === Number(selectedSubcategoryId)
      )
    ) {
      setSelectedSubcategoryId(null);
    }
  }, [selectedCategorySubcategories, selectedSubcategoryId]);

  const pageTitle = [selectedCategoryLabel || routeCategoryLabel || "Booking category", cityLabel]
    .filter(Boolean)
    .join(" in ");
  const compactFilterControlSx = React.useMemo(
    () => ({
      minWidth: { xs: "100%", sm: 220 },
      "& .MuiOutlinedInput-root": {
        minHeight: 40,
        borderRadius: 999,
        backgroundColor: alpha(theme.palette.primary.main, 0.035),
        fontWeight: 600,
        "& fieldset": {
          borderColor: alpha(theme.palette.primary.main, 0.16),
        },
        "&:hover fieldset": {
          borderColor: alpha(theme.palette.primary.main, 0.26),
        },
        "&.Mui-focused fieldset": {
          borderColor: theme.palette.primary.main,
          borderWidth: 1,
        },
      },
      "& .MuiSelect-select": {
        py: 1,
      },
    }),
    [theme]
  );
  const seoDescription = `Browse public booking listings${
    cityLabel ? ` in ${cityLabel}` : ""
  }${selectedCategoryLabel ? ` for ${selectedCategoryLabel}` : ""} on Timely365.`;
  const seoUrl =
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}`
      : "/booking";

  return (
    <Container sx={{ py: 4 }}>
      <SeoMeta
        title={`${pageTitle || "Booking category"} | Timely365`}
        description={seoDescription}
        url={seoUrl}
      />
      <Button
        variant="text"
        onClick={() => navigate("/booking")}
        sx={{ mb: 1.5, px: 0, textTransform: "none", fontWeight: 700 }}
      >
        Back to Booking
      </Button>
      <Typography variant="h4" fontWeight={800} sx={{ mb: 1 }}>
        {pageTitle || "Booking category"}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Browse public booking listings for this city and refine the list by category when needed.
      </Typography>
      {cityLabel ? (
        <Chip
          size="small"
          label={cityLabel}
          sx={{
            mb: 2,
            fontWeight: 700,
            bgcolor: alpha(theme.palette.secondary.main, 0.12),
            color: "secondary.main",
          }}
        />
      ) : null}

      <Paper
        variant="outlined"
        sx={{
          mb: 3,
          p: { xs: 2, md: 2.25 },
          borderRadius: 3,
          borderColor: alpha(theme.palette.primary.main, 0.16),
          backgroundColor: alpha(theme.palette.primary.main, 0.025),
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", lg: "center" }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "stretch", sm: "center" }}
              useFlexGap
              flexWrap="wrap"
              sx={{ flex: 1, minWidth: 0 }}
            >
              {availableTopCategories.length > 0 ? (
                <FormControl size="small" sx={compactFilterControlSx}>
                  <Select
                    displayEmpty
                    value={selectedCategoryId ?? ""}
                    onChange={(event) => {
                      const nextValue = Number(event.target.value || 0);
                      setSelectedCategoryId(nextValue > 0 ? nextValue : null);
                      setSelectedSubcategoryId(null);
                    }}
                    renderValue={(value) =>
                      value ? selectedCategoryLabel || "Category" : "Category"
                    }
                  >
                    <MenuItem value="">All categories</MenuItem>
                    {availableTopCategories.map((categoryItem) => (
                      <MenuItem
                        key={Number(categoryItem?.id || 0)}
                        value={Number(categoryItem?.id || 0)}
                      >
                        {getBookingCategoryDisplayName(categoryItem)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}

              {selectedCategoryId && selectedCategorySubcategories.length > 0 ? (
                <FormControl size="small" sx={compactFilterControlSx}>
                  <Select
                    displayEmpty
                    value={selectedSubcategoryId ?? ""}
                    onChange={(event) => {
                      const nextValue = Number(event.target.value || 0);
                      setSelectedSubcategoryId(nextValue > 0 ? nextValue : null);
                    }}
                    renderValue={(value) =>
                      value ? selectedSubcategoryLabel || "Subcategory" : "Subcategory"
                    }
                  >
                    <MenuItem value="">All in {selectedCategoryLabel || "category"}</MenuItem>
                    {selectedCategorySubcategories.map((subcategory) => (
                      <MenuItem
                        key={Number(subcategory?.id || 0)}
                        value={Number(subcategory?.id || 0)}
                      >
                        {getBookingCategoryDisplayName(subcategory)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
            </Stack>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent={{ xs: "flex-start", lg: "flex-end" }}
              useFlexGap
              flexWrap="wrap"
            >
              <Chip
                size="small"
                label={`${filteredBusinesses.length} match${filteredBusinesses.length === 1 ? "" : "es"}`}
                sx={{
                  fontWeight: 700,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: "primary.main",
                }}
              />
              {BOOKING_LISTING_FILTERS.map((filterOption) => {
                const selected = listingFilter === filterOption.value;
                return (
                  <Button
                    key={filterOption.value}
                    size="small"
                    variant={selected ? "contained" : "outlined"}
                    color={selected ? "primary" : "inherit"}
                    onClick={() => setListingFilter(filterOption.value)}
                    sx={{
                      minWidth: 78,
                      borderRadius: 999,
                      textTransform: "none",
                      px: 1.1,
                    }}
                  >
                    {filterOption.label}
                  </Button>
                );
              })}
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      {!loading && (
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} sx={{ padding: "1rem 0" }}>
            {filteredBusinesses.map((business: BookingMarketplaceBusiness) => (
              <Box key={business.id} sx={{ mb: 1.5 }}>
                <CategoryBusinessCard
                  business={business}
                  linkOverride={`/${business.url}`}
                />
              </Box>
            ))}

            {!filteredBusinesses.length && (
              <Typography color="text.secondary">
                No public booking listings were found for this city with the current filters.
              </Typography>
            )}
          </Grid>
        </Grid>
      )}
    </Container>
  );
}
